import { HistoryItem } from '../history';
import { OtherString } from '../otherString';
import { ParsedDate } from '../parsed/parsedDate';
import { ParsedNumber } from '../parsed/parsedNumber';
import { Prettify } from '../prettify';
import { PagedRequest } from '../request/pagedRequest';
import { AssetLocationKind } from './assetLocationKind';
import { AssetType } from './assetType';
import { Properties } from './properties';
import { z } from 'zod';

export const Asset = z.object({
    id: z.string(),
    parentId: z.string().nullish(),
    productNumber: z.string().nullish(),
    serialNumber: z.string().nullish(),
    type: AssetType.or(OtherString).default(''),
    name: z.string().default(''),
    locationType: AssetLocationKind.or(OtherString).nullish(),
    locationId: z.string().nullish(),
    properties: Properties.nullish(),
    lifeCycle: z
        .object({
            start: z.coerce.date().nullish(),
            end: z.coerce.date().nullish(),
        })
        .nullish(),
    history: HistoryItem.array().nullish(),
    createdOn: z.coerce.date().nullish(),
    modifiedOn: z.coerce.date().nullish(),
    deletedOn: z.coerce.date().nullish(),
});
export type Asset = Prettify<z.infer<typeof Asset>>;

export const REGEX_ASSET_PRODUCT_NUMBER = /\d{3}-\d{3}-\d{4}-\d{2}-\d{2}/;
export const REGEX_ASSET_SERIAL_NUMBER = /\d{3}-\d{3}-\d{2}-\d{6}/;
export const AssetDefect = z.object({
    assetDefectTicketGuid: z.string().nullish(),
    storeGuid: z.string().nullish(),
    assetGuid: z.string().nullish(),
    reasons: z.string().array().nonempty().nullish(),
    reasonsTechnicalCodes: z.tuple([z.string(), z.string()]).nullish(),
    note: z.string().nullish(),
    modifiedBy: z.string().nullish(),
    externalAssetDefectTicket: z
        .object({
            id: ParsedNumber.nullish(),
            priority: ParsedNumber.nullish(),
            status: ParsedNumber.nullish(),
            repairDate: ParsedDate.nullish(),
            returnDate: ParsedDate.nullish(),
            hasReachedPendingStatus: z.boolean().nullish(),
            hasReachedRepairInProgressStatus: z.boolean().nullish(),
            resolution: z.string().nullish(),
            deliveryTrackingId: z.string().nullish(),
            createdOn: ParsedDate.nullish(),
            modifiedOn: ParsedDate.nullish(),
        })
        .nullish(),
    createdOn: ParsedDate.nullish(),
    modifiedOn: ParsedDate.nullish(),
});
export type AssetDefect = Prettify<z.infer<typeof AssetDefect>>;

export const PagedAssetDefectResponse = PagedRequest(AssetDefect);
export type PagedAssetDefectResponse = z.infer<typeof PagedAssetDefectResponse>;

export const AssetDefectRequestQuery = z.object({
    storeGuid: z.string(),
    assetGuid: z.string(),
    limit: z.int().nullish(),
});
export type AssetDefectRequestQuery = z.infer<typeof AssetDefectRequestQuery>;

export const CreateAssetDefectTicketRequest = z.object({
    storeGuid: z.string(),
    assetGuid: z.string(),
    assetType: z.string().nullish(),
    reasons: z.string().array().nullish(),
    reasonsTechnicalCodes: z.string().array().nullish(),
    note: z.string().nullish(),
    image: z.string().nullish(),
    modifiedBy: z.string().nullish(),
});
export type CreateAssetDefectTicketRequest = z.infer<typeof CreateAssetDefectTicketRequest>;

export const CancelAssetDefectTicketRequest = z.object({
    assetDefectTicketGuid: z.string(),
    modifiedBy: z.string().nullish(),
});
export type CancelAssetDefectTicketRequest = z.infer<typeof CancelAssetDefectTicketRequest>;
